import { useState } from 'react'
import { ArrowLeftIcon, BanknoteIcon, CreditCardIcon } from '../icons'
import { formatTRY } from '../../utils/money'
import { parseAmount } from './BahsisPanel'
import { AmountField } from './AmountField'
import type { PaymentMethod } from '../../types'

interface Props {
  totalKurus: number
  /** Whether the sale that follows also prints; carried through untouched. */
  print: boolean
  onPick: (method: PaymentMethod, print: boolean) => void
  onCancel: () => void
}

/**
 * Cash or card, asked once the money is about to be taken.
 *
 * A till never asked: the question at the counter was whether the
 * money was taken, not what it was taken in, and every sale recorded `paid`.
 * A restaurant has to answer it, at close the drawer and the card terminal
 * are counted separately, and a day that lumps them together cannot be
 * reconciled against either.
 *
 * Opens under the pay buttons rather than as a modal over the screen: the
 * cart is still the thing being paid for and covering it to ask a two-word
 * question reads as a bigger interruption than it is. The panel slides down
 * from the row that was just pressed, so the answer appears where the hand
 * already is.
 *
 * Cash sits left and card right, and that never changes: a hand that learns
 * the side stops reading the label.
 *
 * Nakit seçilince para üstü adımı (kullanıcı, 5 Eki 2026): alınan para
 * yazılır ya da hazır banknot düğmesine basılır, para üstü büyük yazıyla
 * görünür. Kayda geçmiyor, yalnızca kasiyerin hesabı; boş bırakıp onaylamak
 * "tam para" demek, yani acelesi olan bir adım fazlasıyla kapatıyor.
 */
export function TenderPicker({ totalKurus, print, onPick, onCancel }: Props) {
  const [cash, setCash] = useState(false)
  const [given, setGiven] = useState('')

  if (cash) {
    const givenKurus = parseAmount(given)
    const change = givenKurus === null ? null : givenKurus - totalKurus
    const short = change !== null && change < 0
    const bills = billsFor(totalKurus)
    const confirm = () => {
      if (!short) onPick('cash', print)
    }

    return (
      <div className="tender" role="group" aria-label="Nakit ödeme">
        <div className="tender__head">
          <span className="tender__q">Alınan para</span>
          <span className="tender__amount">{formatTRY(totalKurus)}</span>
        </div>

        <AmountField
          value={given}
          onChange={setGiven}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              confirm()
            }
          }}
          onFocus={(e) => {
            const panel = e.currentTarget.closest('.tender')
            window.setTimeout(
              () => panel?.scrollIntoView({ block: 'end', behavior: 'smooth' }),
              350,
            )
          }}
          ariaLabel="Alınan tutar"
        />

        {bills.length > 0 && (
          <div className="change__bills">
            {bills.map((k) => (
              <button
                key={k}
                type="button"
                className={givenKurus === k ? 'is-on' : ''}
                onClick={() => setGiven(String(k / 100))}
              >
                {(k / 100).toLocaleString('tr-TR')} ₺
              </button>
            ))}
          </div>
        )}

        <div className="change__out" aria-live="polite">
          {short ? (
            <span className="bahsis__warn">Alınan tutar adisyondan {formatTRY(-change!)} az.</span>
          ) : (
            <>
              <span>Para üstü</span>
              <b>{formatTRY(change ?? 0)}</b>
            </>
          )}
        </div>

        <div className="change__actions">
          <button
            type="button"
            className="change__back"
            onClick={() => setCash(false)}
            aria-label="Geri"
          >
            <ArrowLeftIcon />
          </button>
          <button type="button" className="change__ok" onClick={confirm} disabled={short}>
            <BanknoteIcon />
            Nakit alındı, kapat
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="tender" role="group" aria-label="Ödeme yöntemi">
      <div className="tender__head">
        <span className="tender__q">Nasıl alındı?</span>
        <span className="tender__amount">{formatTRY(totalKurus)}</span>
      </div>

      <div className="tender__opts">
        <button type="button" className="tender__opt" onClick={() => setCash(true)}>
          <BanknoteIcon />
          Nakit
        </button>
        <button type="button" className="tender__opt" onClick={() => onPick('card', print)}>
          <CreditCardIcon />
          Kredi Kartı
        </button>
      </div>

      <button type="button" className="tender__cancel" onClick={onCancel}>
        Vazgeç
      </button>
    </div>
  )
}

/**
 * Hazır tutar düğmeleri (kullanıcı, 5 Eki 2026): adisyonun üstündeki en
 * yakın 50'nin katı ve en yakın yüzlük. İkisi aynıysa tek düğme. 380 ₺'ye
 * 400; 150 ₺'ye 200; 2.615 ₺'ye 2.650 ve 2.700; 400 ₺'ye hiç (tam verilir).
 * Daha uzağı önerilmiyor (380'e ya da 400'e 500 saçma).
 * Kuruş yok; tam para için kutu boş bırakılıyor (içinde yazı yok, sönük 0).
 */
export function billsFor(totalKurus: number): number[] {
  const next = (step: number) => Math.floor(totalKurus / step) * step + step
  // Yüzlük adisyonu (400 ₺) müşteri tam veriyor: öneri yok. 50'lik
  // adisyona (150 ₺) yalnız bir üst yüzlük. Adisyondan 100 ₺ ya da daha
  // fazla yukarısı hiç önerilmiyor.
  if (totalKurus % 10000 === 0) return []
  const cands = totalKurus % 5000 === 0 ? [next(10000)] : [next(5000), next(10000)]
  const near = cands.filter((k) => k - totalKurus < 10000)
  return near.filter((k, i) => near.indexOf(k) === i)
}
